import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import { Truck, Check, Package, Mail, MapPin, PartyPopper } from "lucide-react";

const FLOW = [
  { key: "created", label: "Created", icon: Check },
  { key: "printing", label: "Printing", icon: Package },
  { key: "mailed", label: "Mailed", icon: Mail },
  { key: "in_transit", label: "In Transit", icon: MapPin },
  { key: "delivered", label: "Delivered", icon: PartyPopper },
];

const Deliveries = () => {
  const [cards, setCards] = useState([]);

  const load = useCallback(async () => {
    const res = await api.get("/cards");
    setCards(res.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const advance = async (card) => {
    const idx = FLOW.findIndex((f) => f.key === (card.delivery?.status || "created"));
    const next = FLOW[Math.min(idx + 1, FLOW.length - 1)];
    await api.put(`/cards/${card.id}/delivery`, { status: next.key });
    await load();
  };

  const sent = cards.filter((c) => c.delivery && !["draft", null, undefined].includes(c.delivery.status));

  return (
    <AppShell>
      <div className="mb-8">
        <h1 className="font-serif text-4xl mb-1">Sent with Love</h1>
        <p className="text-muted-foreground">Track every card from press to doorstep — physical or digital.</p>
      </div>

      <div className="glass rounded-xl p-4 mb-6 text-sm text-muted-foreground flex items-center gap-2">
        <Truck className="w-4 h-4 text-secondary" />
        Live logistics via <span className="text-foreground font-medium">Lob</span> — status auto-syncs from the delivery webhook.
      </div>

      {sent.length === 0 ? (
        <div className="glass rounded-2xl p-12 text-center">
          <Truck className="w-12 h-12 mx-auto mb-4 text-secondary/60" />
          <h3 className="font-serif text-2xl mb-2">Nothing sent yet</h3>
          <p className="text-muted-foreground">Generate a card and approve it (digital or physical) to start tracking.</p>
        </div>
      ) : (
        <div className="space-y-5" data-testid="deliveries-list">
          {sent.map((c) => {
            const isDigital = c.delivery.method === "digital";
            const currentIdx = FLOW.findIndex((f) => f.key === c.delivery.status);
            return (
              <div key={c.id} className="glass rounded-2xl p-6">
                <div className="flex items-center gap-4 mb-5">
                  <img src={`data:image/png;base64,${c.image_base64}`} alt="" className="w-14 h-14 rounded-lg object-cover" />
                  <div className="flex-1">
                    <div className="font-medium flex items-center gap-2 flex-wrap">
                      {c.loved_one_name} · {c.occasion}
                      <span className={`text-[10px] px-2 py-0.5 rounded-full border ${isDigital ? "bg-primary/15 text-primary border-primary/30" : "bg-secondary/15 text-secondary border-secondary/30"}`}>
                        {isDigital ? "Digital" : "Physical"}
                      </span>
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {isDigital ? "Delivered instantly to inbox" : `Tracking: ${c.delivery.tracking_number || "—"}`}
                    </div>
                  </div>
                  {!isDigital && c.delivery.status !== "delivered" && (
                    <button onClick={() => advance(c)} data-testid="advance-delivery-btn"
                      className="text-sm bg-secondary/20 text-secondary border border-secondary/30 px-3 py-2 rounded-lg hover:bg-secondary/30">
                      Advance status
                    </button>
                  )}
                </div>
                {isDigital ? (
                  <div className="flex items-center gap-2 text-secondary text-sm">
                    <PartyPopper className="w-5 h-5" /> Delivered digitally
                  </div>
                ) : (
                  <div className="flex items-center justify-between">
                    {FLOW.map((step, i) => {
                      const done = i <= currentIdx;
                      const Icon = step.icon;
                      return (
                        <React.Fragment key={step.key}>
                          <div className="flex flex-col items-center gap-1.5">
                            <div className={`w-9 h-9 rounded-full flex items-center justify-center border ${done ? "bg-gradient-to-br from-primary to-secondary border-transparent text-white" : "bg-muted/30 border-border text-muted-foreground"}`}>
                              <Icon className="w-4 h-4" />
                            </div>
                            <span className={`text-[10px] ${done ? "text-foreground" : "text-muted-foreground"}`}>{step.label}</span>
                          </div>
                          {i < FLOW.length - 1 && <div className={`flex-1 h-0.5 mx-1 ${i < currentIdx ? "bg-secondary" : "bg-border"}`} />}
                        </React.Fragment>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </AppShell>
  );
};

export default Deliveries;
